import { useCallback, useEffect, useRef, useState } from 'react'
import { getUxMoments, markUxMoment, type UxMomentKey } from '../api/client'

/**
 * "Has this user ever seen <moment>?" — the read half of the one-time UX
 * moments store (`users.prefs.uxMoments`).
 *
 * Deliberately server-backed rather than localStorage: the same person reaches
 * this app from localhost, from the box's three HTTPS clone origins, and from
 * their phone, and a lifetime dialog kept locally would fire once per origin
 * per device.
 *
 * Usage:
 *   const primer = useOnceEver(UX_MOMENT_KEYS.locationPermissionPrimer)
 *   if (primer.status === 'unseen') { ...show it, then primer.markSeen() }
 *
 * `status` is three-valued on purpose. While it's 'loading' we don't yet know,
 * and showing a once-ever dialog optimistically would show it to someone who
 * has already dismissed it — the one thing this hook exists to prevent. Callers
 * should render nothing until the answer arrives.
 *
 * A failed fetch settles on 'seen', not 'unseen': if we can't tell, staying
 * quiet is the better failure. The caller loses one primer; the alternative is
 * re-nagging someone who already said no.
 */
type OnceEverStatus = 'loading' | 'seen' | 'unseen'

export interface OnceEver {
    status: OnceEverStatus
    /** Record the moment. Optimistic, idempotent, and safe to call twice — the
     *  server keeps the first timestamp. */
    markSeen: () => Promise<void>
}

export function useOnceEver(key: UxMomentKey): OnceEver {
    const [status, setStatus] = useState<OnceEverStatus>('loading')
    // Guards a setState after unmount, and stops a slow fetch from resurrecting
    // 'unseen' after markSeen() has already flipped us to 'seen'.
    const settled = useRef(false)

    useEffect(() => {
        let cancelled = false
        settled.current = false
        setStatus('loading')

        getUxMoments()
            .then((moments) => {
                if (cancelled || settled.current) return
                setStatus(moments.seen[key] ? 'seen' : 'unseen')
            })
            .catch(() => {
                if (cancelled || settled.current) return
                setStatus('seen')
            })

        return () => {
            cancelled = true
        }
    }, [key])

    const markSeen = useCallback(async () => {
        settled.current = true
        setStatus('seen')
        try {
            await markUxMoment(key)
        } catch {
            // Swallowed by design. The moment has been shown either way, and
            // the worst case is that it shows once more on another device.
        }
    }, [key])

    return { status, markSeen }
}
